import {
  documentGroupPages,
  documentsInfiniteQuery,
} from "@common/api/documents"
import { ensureListData } from "@common/api/utils"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { createContext, useContext, useState } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { DocumentTable, type Picked } from "../-components/document-table"
import { DocumentsUploadArea } from "../-components/documents-upload-area"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
  FIXED_COLUMNS,
} from "../-lib/columns"
import {
  documentFilterConfigs,
  hasDocumentFilters,
  NO_DOCUMENT_FILTERS,
} from "../-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "../-lib/grouping"
import { writeDocumentsVisibility } from "../-lib/preferences"
import {
  type DocumentsDisplay,
  type DocumentsSearch,
  displayOf,
  documentPagesParams,
  parseDocumentsSearch,
  TABS,
  type Tab,
  tabLabel,
  uploadVisibility,
} from "../-lib/search"

type DocumentsSelectionContextValue = {
  clearDocumentSelection: () => void
}

export const DocumentsSelectionContext =
  createContext<DocumentsSelectionContextValue | null>(null)

export function useDocumentsSelectionContext(): DocumentsSelectionContextValue {
  const context = useContext(DocumentsSelectionContext)
  if (context === null) {
    throw new Error(
      "useDocumentsSelectionContext requires DocumentsSelectionContext",
    )
  }
  return context
}

export const Route = createFileRoute("/_layout/documents/_list")({
  validateSearch: parseDocumentsSearch,
  loaderDeps: ({ search }) => ({
    pages: documentPagesParams(search),
    grouped: Boolean(displayOf(search).group && !search.search),
  }),
  loader: ({ context: { queryClient }, deps }) =>
    ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(documentGroupPages.groups(deps.pages)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(documentsInfiniteQuery(deps.pages)),
    }),
  component: DocumentsPage,
})

function useUpdateSearch(): (patch: Partial<DocumentsSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function DocumentsPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const [picked, setPicked] = useState<Picked>(new Set())

  return (
    <DocumentsSelectionContext.Provider
      value={{ clearDocumentSelection: () => setPicked(new Set()) }}
    >
      <DocumentsUploadArea visibility={uploadVisibility(search.tab ?? "user")}>
        <ListPage
          toolbar={<Toolbar display={display} hidden={fit.hidden} />}
          detail={<Outlet />}
          scrollRef={ref}
        >
          <ActiveFilters />
          <DocumentTable
            search={search}
            display={display}
            columns={fit.shown}
            picked={picked}
            onPick={setPicked}
            onSort={(column) => update(sortSearch(display, column))}
            onClear={() =>
              update({ ...NO_DOCUMENT_FILTERS, search: undefined })
            }
          />
        </ListPage>
      </DocumentsUploadArea>
    </DocumentsSelectionContext.Provider>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: DocumentsDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { tab = "user", search = "" } = values
  const update = useUpdateSearch()

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) => {
            if (next !== "archived") writeDocumentsVisibility(next)
            update({ tab: next })
          }}
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search documents…"
          />
          <FilterButton
            active={hasDocumentFilters(values)}
            configs={documentFilterConfigs(tab)}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={({ group, subgroup }) => update({ group, subgroup })}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={FIXED_COLUMNS}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          <UploadButton tab={tab} />
        </>
      }
    />
  )
}

function sortSearch(
  display: DocumentsDisplay,
  column: Column,
): Partial<DocumentsSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "added" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function columnsSearch(columns: Column[]): Partial<DocumentsSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function UploadButton({ tab }: { tab: Tab }): React.ReactElement | null {
  const canCreate = useHasPermission("document:create")
  if (!canCreate || !uploadVisibility(tab)) return null

  return (
    <Button size="sm" asChild>
      <Link to="/documents/upload" search={true}>
        <Plus />
        Upload
      </Link>
    </Button>
  )
}

function ActiveFilters(): React.ReactElement {
  const { tab = "user", ...values } = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ActiveFilterBar
      active={hasDocumentFilters(values)}
      configs={documentFilterConfigs(tab)}
      values={values}
      onChange={update}
      onClear={() => update(NO_DOCUMENT_FILTERS)}
    />
  )
}
